import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'next-i18next';
import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';
import clsx from 'clsx';
import useLocalizationQuery from '@hooks/localization/use-localization';
import Environment from '@config/environment';
import sanitizer from '@helpers/sanitizer';
import styles from './consignment-form.module.scss';
import ConsignmentFormHeader from './consignment-form-header/consignment-form-header';
import ConsignmentFormFooter from './consignment-form-footer/consignment-form-footer';
import ConsignmentModal from './consignment-modal/consignment-modal';

const isDebug = Environment.envName === 'dev';

const ConsignmentAvailableCountry = [
  { isoCode: 'BE', enName: 'Belgium' },
  { isoCode: 'BG', enName: 'Bulgaria' },
  { isoCode: 'CZ', enName: 'Czech Republic' },
  { isoCode: 'DK', enName: 'Denmark' },
  { isoCode: 'DE', enName: 'Germany' },
  { isoCode: 'EE', enName: 'Estonia' },
  { isoCode: 'IE', enName: 'Ireland' },
  { isoCode: 'GR', enName: 'Greece' },
  { isoCode: 'ES', enName: 'Spain' },
  { isoCode: 'FR', enName: 'France' },
  { isoCode: 'HR', enName: 'Croatia' },
  { isoCode: 'IT', enName: 'Italy' },
  { isoCode: 'CY', enName: 'Cyprus' },
  { isoCode: 'LV', enName: 'Latvia' },
  { isoCode: 'LT', enName: 'Lithuania' },
  { isoCode: 'LU', enName: 'Luxembourg' },
  { isoCode: 'HU', enName: 'Hungary' },
  { isoCode: 'MT', enName: 'Malta' },
  { isoCode: 'US', enName: 'United States' },
  { isoCode: 'NL', enName: 'Netherlands' },
  { isoCode: 'AT', enName: 'Austria' },
  { isoCode: 'PL', enName: 'Poland' },
  { isoCode: 'PT', enName: 'Portugal' },
  { isoCode: 'RO', enName: 'Romania' },
  { isoCode: 'SI', enName: 'Slovenia' },
  { isoCode: 'SK', enName: 'Slovakia' },
  { isoCode: 'FI', enName: 'Finland' },
  { isoCode: 'SE', enName: 'Sweden' },
  { isoCode: 'GB', enName: 'United Kingdom' },
  { isoCode: 'IS', enName: 'Iceland' },
  { isoCode: 'LI', enName: 'Liechtenstein' },
  { isoCode: 'NO', enName: 'Norway' },
];

const FORM_FIELD_MATCHER = {
  country: '00NSZ000009wWoj',
  city: '00NSZ000009wZuh',
  brands: '00NSZ0000093WXd',
  numberOfItems: '00NSZ0000093QnD',
};

const numberOfItemsValues = ['0-15', '15-35', '50+'];

const formActionUrl = 'https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8&orgId=00D0X000000sv3i';

const hiddenInputs = {
  oid: '00D0X000000sv3i',
  retURL: !isDebug ? 'https://www.vestiairecollective.com/' : 'http://locahost:3000',
  debug: !isDebug ? '0' : '1',
  debugEmail: !isDebug ? '' : 'theo.morais@vestiairecollective.com',
};

const ConsignmentForm: React.FC = () => {
  const formRef = React.useRef<HTMLFormElement>(null);
  const { t } = useTranslation();
  const { countryList } = useLocalizationQuery();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const countries = useMemo(() => {
    return ConsignmentAvailableCountry.map((countryCustom) => {
      const countryMatch = countryList.find((country) => country.isoCode === countryCustom.isoCode);
      return { ...countryCustom, name: countryMatch?.name };
    });
  }, [countryList]);

  const buildDefaultValue = () => {
    const formInitValue = {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
    };

    formInitValue[FORM_FIELD_MATCHER.brands] = '';
    formInitValue[FORM_FIELD_MATCHER.city] = '';
    formInitValue[FORM_FIELD_MATCHER.country] = '';
    formInitValue[FORM_FIELD_MATCHER.numberOfItems] = '';

    return formInitValue;
  };

  // Functions
  const Schema = Yup.lazy(() => {
    const objectShape = {
      email: Yup.string().email('emailformat').required('required'),
      first_name: Yup.string().required('required'),
      last_name: Yup.string().required('required'),
      phone: Yup.string(),
    };

    objectShape[FORM_FIELD_MATCHER.brands] = Yup.string().required('required');
    objectShape[FORM_FIELD_MATCHER.city] = Yup.string().required('required');
    objectShape[FORM_FIELD_MATCHER.country] = Yup.string().required('required');
    objectShape[FORM_FIELD_MATCHER.numberOfItems] = Yup.string().required('required');
    return Yup.object().shape(objectShape);
  });

  const closePopup = () => {
    window.location.pathname = '/';
  };

  return (
    <>
      <iframe
        title="validation"
        name="validation"
        style={{ display: 'none' }}
      />
      <Formik
        initialValues={buildDefaultValue()}
        validationSchema={Schema}
        onSubmit={(_, { setSubmitting }) => {
          const form = formRef.current;
          if (form) {
            form.submit();
            setSubmitting(false);
            setIsModalOpen(true);
          }
        }}
        validateOnBlur
      >
        {({ values, errors, touched, isValid, isSubmitting }) => (
          <Form
            ref={formRef}
            acceptCharset="utf-8"
            className={clsx(styles.consignmentForm, { hasLoader: isSubmitting })}
            action={formActionUrl}
            method="POST"
            target="validation"
          >
            <ConsignmentFormHeader></ConsignmentFormHeader>

            <input
              type="hidden"
              name="oid"
              value={hiddenInputs.oid}
            />
            <input
              type="hidden"
              name="retURL"
              value={hiddenInputs.retURL}
            />

            {isDebug && (
              <>
                <input
                  type="hidden"
                  name="debug"
                  value={hiddenInputs.debug}
                />
                <input
                  type="hidden"
                  name="debugEmail"
                  value={hiddenInputs.debugEmail}
                />
              </>
            )}
            {/* Firstname & Lastname*/}
            <div className={clsx(styles.consignmentForm__form__row, styles['consignmentForm__form__row--2cols'])}>
              {/* Firstname */}
              <div
                className={clsx('vc-formUnit', styles['vc-formUnit'], {
                  [styles.error + ' error']: errors.first_name && touched.first_name,
                })}
              >
                <Field
                  as="input"
                  name="first_name"
                  aria-labelledby="label-first_name"
                  className={'vc-inputText'}
                  value={values.first_name}
                  maxLength={50}
                  required
                ></Field>
                <label
                  id="label-first_name"
                  className={clsx('vc-inputText-label', styles['vc-inputText-label'], styles.required)}
                >
                  {t('CONSIGNMENT.FORM.LABEL.FIRSTNAME')}
                </label>
              </div>
              {/* Lastname */}
              <div
                className={clsx('vc-formUnit', styles['vc-formUnit'], {
                  [styles.error + ' error']: errors.last_name && touched.last_name,
                })}
              >
                <Field
                  as="input"
                  name="last_name"
                  aria-labelledby="label-last_name"
                  className={'vc-inputText'}
                  value={values.last_name}
                  maxLength={50}
                  required
                ></Field>
                <label
                  id="label-last_name"
                  className={clsx('vc-inputText-label', styles['vc-inputText-label'], styles.required)}
                >
                  {t('CONSIGNMENT.FORM.LABEL.LASTNAME')}
                </label>
              </div>
            </div>
            {/* email */}
            <div
              className={clsx('vc-formUnit', styles['vc-formUnit'], {
                [styles.error + ' error']: errors.email && touched.email,
              })}
            >
              <Field
                as="input"
                name="email"
                aria-labelledby="label-email"
                className={'vc-inputText'}
                value={values.email}
                maxLength={50}
                required
              ></Field>
              <label
                id="label-email"
                className={clsx('vc-inputText-label', styles['vc-inputText-label'], styles.required)}
              >
                {t('CONSIGNMENT.FORM.LABEL.EMAIL')}
              </label>
            </div>
            {/* Country */}
            <div
              className={clsx(
                'vc-formUnit',
                'vc-formUnit--select',
                styles['vc-formUnit'],
                styles['vc-formUnit--select'],
                {
                  [styles.error + ' error']: errors[FORM_FIELD_MATCHER.country] && touched[FORM_FIELD_MATCHER.country],
                },
              )}
            >
              <Field
                as="select"
                name={FORM_FIELD_MATCHER.country}
                aria-labelledby="label-country"
                className={'vc-inputText'}
                value={values[FORM_FIELD_MATCHER.country]}
                required
              >
                <option
                  value=""
                  disabled
                >
                  {t('CONSIGNMENT.FORM.PLACEHOLDER.COUNTRY')}
                </option>
                {countries?.map((c) => (
                  <option
                    key={c.isoCode}
                    value={c.enName}
                  >
                    {c.name}
                  </option>
                ))}
              </Field>
              <label
                id="label-country"
                className={clsx('vc-inputText-label', styles['vc-inputText-label'], styles.required)}
              >
                {t('CONSIGNMENT.FORM.LABEL.COUNTRY')}
              </label>
            </div>
            {/* City */}
            <div
              className={clsx('vc-formUnit', styles['vc-formUnit'], {
                [styles.error + ' error']: errors[FORM_FIELD_MATCHER.city] && touched[FORM_FIELD_MATCHER.city],
              })}
            >
              <Field
                as="input"
                name={FORM_FIELD_MATCHER.city}
                aria-labelledby="label-city"
                className={'vc-inputText'}
                value={values[FORM_FIELD_MATCHER.city]}
                maxLength={50}
                required
              ></Field>
              <label
                id="label-city"
                className={clsx('vc-inputText-label', styles['vc-inputText-label'], styles.required)}
              >
                {t('CONSIGNMENT.FORM.LABEL.CITY')}
              </label>
            </div>
            {/* Phone */}
            <div
              className={clsx('vc-formUnit', styles['vc-formUnit'], {
                [styles.error + ' error']: errors.phone && touched.phone,
              })}
            >
              <Field
                as="input"
                name="phone"
                aria-labelledby="label-phone"
                className={'vc-inputText'}
                value={values.phone}
                maxLength={50}
              ></Field>
              <label
                id="label-phone"
                className={clsx('vc-inputText-label', styles['vc-inputText-label'])}
              >
                {t('CONSIGNMENT.FORM.LABEL.PHONE')}
              </label>
            </div>
            {/* Brands */}
            <label
              id="label-brands"
              className={clsx('vc-inputText-label', styles['vc-inputText-label'], styles.required)}
            >
              {t('CONSIGNMENT.FORM.LABEL.BRANDS')}
              <br />
              <Trans i18nKey="CONSIGNMENT.FORM.LABEL.BRANDSLINK">
                See brands by category
                <a
                  href="/journal/vip-consignment-brands-list/"
                  target="_blank"
                  className={clsx(styles.brandsListLink)}
                >
                  here
                </a>
              </Trans>
            </label>
            <div
              className={clsx('vc-formUnit', 'vc-formUnit', styles['vc-formUnit'], styles['vc-formUnit'], {
                [styles.error + ' error']: errors[FORM_FIELD_MATCHER.brands] && touched[FORM_FIELD_MATCHER.brands],
              })}
            >
              <fieldset
                role="group"
                className={clsx(styles.brandsRadio)}
              >
                <ol>
                  <li>
                    <Field
                      id="brands-luxe"
                      className={styles['radio-option__input']}
                      type="radio"
                      name={FORM_FIELD_MATCHER.brands}
                      value="Luxe"
                    />
                    <label
                      htmlFor="brands-luxe"
                      dangerouslySetInnerHTML={{
                        __html: sanitizer(`
                          ${t('CONSIGNMENT.FORM.LABEL.LUXE')}
                          <br />
                          <span>${t('CONSIGNMENT.FORM.LABEL.LUXELIST')}</span>
                        `),
                      }}
                    ></label>
                  </li>
                  <li>
                    <Field
                      className={styles['radio-option__input']}
                      id="brands-designer"
                      type="radio"
                      name={FORM_FIELD_MATCHER.brands}
                      value="Designer"
                    />
                    <label
                      htmlFor="brands-designer"
                      dangerouslySetInnerHTML={{
                        __html: sanitizer(`
                          ${t('CONSIGNMENT.FORM.LABEL.DESIGNER')}
                          <br />
                          <span>${t('CONSIGNMENT.FORM.LABEL.DESIGNERLIST')}</span>
                        `),
                      }}
                    />
                  </li>
                  <li>
                    <Field
                      className={styles['radio-option__input']}
                      id="brands-premium"
                      type="radio"
                      name={FORM_FIELD_MATCHER.brands}
                      value="Premium"
                    />
                    <label
                      htmlFor="brands-premium"
                      dangerouslySetInnerHTML={{
                        __html: sanitizer(`
                          ${t('CONSIGNMENT.FORM.LABEL.PREMIUM')}
                          <br />
                          <span>${t('CONSIGNMENT.FORM.LABEL.PREMIUMLIST')}</span>
                        `),
                      }}
                    />
                  </li>
                  <li>
                    <Field
                      className={styles['radio-option__input']}
                      id="brands-other"
                      type="radio"
                      name={FORM_FIELD_MATCHER.brands}
                      value="Other"
                    />
                    <label htmlFor="brands-other">{t('CONSIGNMENT.FORM.LABEL.OTHER')}</label>
                  </li>
                </ol>
              </fieldset>
            </div>
            {/* Number of Items */}
            <div
              className={clsx(
                'vc-formUnit',
                'vc-formUnit--select',
                styles['vc-formUnit'],
                styles['vc-formUnit--select'],
                {
                  [styles.error + ' error']:
                    errors[FORM_FIELD_MATCHER.numberOfItems] && touched[FORM_FIELD_MATCHER.numberOfItems],
                },
              )}
            >
              <Field
                as="select"
                name={FORM_FIELD_MATCHER.numberOfItems}
                aria-labelledby="label-numberOfItems"
                className={'vc-inputText'}
                value={values[FORM_FIELD_MATCHER.numberOfItems]}
                required
              >
                <option
                  value=""
                  disabled
                >
                  {t('CONSIGNMENT.FORM.PLACEHOLDER.COUNTRY')}
                </option>
                {numberOfItemsValues.map((c) => (
                  <option
                    key={c}
                    value={c}
                  >
                    {c}
                  </option>
                ))}
              </Field>
              <label
                id="label-numberOfItems"
                className={clsx('vc-inputText-label', styles['vc-inputText-label'], styles.required)}
              >
                {t('CONSIGNMENT.FORM.LABEL.NUMBER_OF_ITEMS')}
              </label>
            </div>

            <ConsignmentFormFooter></ConsignmentFormFooter>

            <div className={clsx(styles.submitBtn)}>
              <input
                className={clsx('vc-btn', 'vc-btn--primary', 'vc-btn--large', styles.proSellersForm__form__submit)}
                type="submit"
                name="Submit"
                disabled={!isValid}
                value={t('CONSIGNMENT.FORM.SUBMIT_BUTTON')}
              />
            </div>
          </Form>
        )}
      </Formik>

      <ConsignmentModal
        isOpen={isModalOpen}
        onClose={closePopup}
        text={t('CONSIGNMENT.MODAL.SUCCESS.TITLE')}
        title={t('CONSIGNMENT.MODAL.SUCCESS.TEXT')}
      ></ConsignmentModal>
    </>
  );
};

export default ConsignmentForm;
